import { Stream } from './core/stream.js';
import { Selection } from './core/selection.js';
import { api } from './core/api.js';
import { auth } from './core/auth.js';
import { opsBus } from './core/ops_bus.js';
import { visibleInterval } from './core/visibility.js';
import { STREAM_RECONNECT_MS, FOCUS_FLASH_MS } from './core/timing.js';
import { attachHlsLive, toggleHlsDrawer } from './views/hls_live.js';
import { toast } from './core/toast.js';
import { setIcon } from './core/icons.js';
import { MUSIC_ENABLED } from './core/flags.js';
import { loadCapabilities, gate, caps } from './core/capabilities.js';
import { isSupported as undockSupported, isUndocked, toggleUndock } from './core/undock.js';
import { initShellOpen } from './core/shell-open.js';
import { addMagnet, addTorrentBlob } from './core/torrent-add.js';
import { showModal } from './core/modal.js';

import { Sidebar } from './views/sidebar.js';
import { Toolbar } from './views/toolbar.js';
import { KpiStrip } from './views/kpi.js';
import { StatusBar } from './views/statusbar.js';
import { mountCoverHover } from './render/coverHover.js';

/**
 * Wires everything together: sidebar nav, torrents/files views, KPI cards,
 * status bar, toolbar, SSE stream.
 *
 * Bootstraps auth before anything else — if the user has no valid token
 * (and refresh fails), this redirects to /login and never returns.
 */
export async function initClient() {
  await auth.bootstrap();
  const me = auth.getUser();
  if (me) {
    const el = document.getElementById('user-label');
    if (el) el.textContent = me.username;
    const ini = document.getElementById('avatar-initial');
    if (ini) ini.textContent = (me.username || '?').trim().charAt(0).toUpperCase() || '?';
  }
  // B2: load the effective-capability manifest (best-effort). Until the engine
  // endpoint is live the fetch falls back silently, so the UI still gates on the
  // legacy flags; B3 migrates the gates onto caps.has(...).
  if (me) await loadCapabilities();

  const [
    { TorrentsView },
    { FilesView },
    { LibraryView },
    { MusicView },
    { PhotoView },
    { AdminUsersView },
    { AdminSharesView },
    { AdminInvitesView },
    { AdminRvideoView },
    { AdminExtView },
    { AdminCollectionsView },
    { AdminPathsView },
    { AdminStorageView },
    { AdminNodesView },
    { DiscoverView },
    { RssView },
    { HistoryView },
    { SettingsView },
    { OpsView },
    { ZapdEngineView },
    { DashboardView },
    { DedupView },
  ] = await Promise.all([
    import('./views/torrents.js'),
    import('./views/files.js'),
    import('./views/library.js'),
    import('./views/music.js'),
    import('./views/photos.js'),
    import('./views/admin_users.js'),
    import('./views/admin_shares.js'),
    import('./views/admin_invites.js'),
    import('./views/admin_rvideo.js'),
    import('./views/admin_ext.js'),
    import('./views/admin_collections.js'),
    import('./views/admin_paths.js'),
    import('./views/admin_storage.js'),
    import('./views/admin_nodes.js'),
    import('./views/discover.js'),
    import('./views/rss.js'),
    import('./views/history.js'),
    import('./views/settings.js'),
    import('./views/ops.js'),
    import('./views/zapd_engine.js'),
    import('./views/dashboard.js'),
    import('./views/dedup.js'),
  ]);

  // R1b.next: in the native shell, listen for .torrent/magnet opens (Finder,
  // TopBar, magnet: scheme) that the shell forwards as an openExternal event, and
  // proactively push the access token so its native uploader has a Bearer.
  // Unconditional: the shim injects window.zapShell AFTER the SPA boots, so an
  // isEmbeddedShell() gate here races the injection (won at 15:26, lost at 18:31
  // — held opens with tokenLen=0 forever). initShellOpen polls for the shell.
  initShellOpen({
    onTorrentImported: () => setTimeout(() => reconnectStream(), STREAM_RECONNECT_MS),
  });

  // -------- icons in static markup --------
  document.querySelectorAll('[data-tabler-icon]').forEach((el) => {
    setIcon(el, el.dataset.tablerIcon, { size: parseInt(el.dataset.size || '16') });
  });

  // -------- core --------
  const stream = new Stream();
  const selection = new Selection();

  // -------- views --------
  const kpi = new KpiStrip();
  const statusbar = new StatusBar();
  const sidebar = new Sidebar({ onView: (name) => switchView(name) });
  // Phase 6: show admin-only sidebar entries (Adult etc.) once auth is up.
  sidebar.applyRole(me?.role || 'user');
  // B3: once the capability manifest is loaded, gate the nav on effective caps
  // ([data-cap] + legacy [data-admin-only] -> user.manage). No-op until live.
  sidebar.applyCapabilities();

  // ── OpsEventBus für Live-Counter (dedup-new, ops-live) ────────────────
  opsBus.start();

  // Tab-hidden-Attribut fuer CSS: Endlos-Pulse-Animationen (Status-Bar,
  // Library-Dot, ops-Badge) pausieren, solange niemand hinschaut.
  const syncHiddenAttr = () => {
    document.documentElement.toggleAttribute('data-tab-hidden', document.hidden);
  };
  document.addEventListener('visibilitychange', syncHiddenAttr);
  syncHiddenAttr();

  // dedup-new bumpen sobald ein high-confidence Pair erkannt wurde (>= 0.95).
  // Niedrige Confidence (SUSPICIOUS_VISUAL_MATCH 0.6, PROBABLE_DUPLICATE 0.8)
  // sind Drilldown-Items im Dedup-Tab und gehören nicht ins Sidebar-Signal.
  // (Das Decrement bei eigener Action passiert in DedupView selbst.)
  opsBus.subscribe(
    (evt) =>
      evt.event === 'pair_found' &&
      evt.source === 'cron_dedup_match' &&
      (evt.data?.confidence ?? 0) >= 0.95,
    () => sidebar.bumpDedupNew(+1),
  );

  // ops-live: Events/Minute alle 2s aktualisieren — nur bei sichtbarem Tab.
  const cancelOpsRate = visibleInterval(() => {
    sidebar.setOpsRate(opsBus.getRatePerMinute());
  }, 2000);

  // ── HLS Live (active streams sidebar badge + drawer) — admin only ────
  // Live-Sessions-Liste ist kein consumer-feature (User explicit 2026-05-04).
  // Backend gibt /api/hls/list für non-admin 403 zurück; ohne den Skip hier
  // würde der Poll permanent 403en.
  let detachHlsLive = () => {};
  if (me?.role === 'admin') {
    detachHlsLive = attachHlsLive({
      onCount: (n) => sidebar.setHlsLive(n),
    });
    // Click on the "Live" sidebar button: open drawer instead of switching view.
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest?.('.nav__item[data-view="hls-live"]');
        if (!btn) return;
        e.stopPropagation();
        e.preventDefault();
        toggleHlsDrawer(btn);
      },
      true,
    );
  }

  // Cleanup-Hook: Bus + Rate-Timer stoppen, wenn der User explizit ausloggt.
  // (Browser-close räumt automatisch auf.)
  const _origLogout = auth.logout?.bind(auth);
  if (_origLogout) {
    auth.logout = function (...args) {
      try {
        cancelOpsRate();
        opsBus.stop();
      } catch (_) {}
      try {
        detachHlsLive();
      } catch (_) {}
      return _origLogout(...args);
    };
  }

  const filesView = new FilesView();
  const libraryView = new LibraryView();
  const musicView = new MusicView();
  const photoView = new PhotoView();
  const adminUsersView = new AdminUsersView();
  const adminSharesView = new AdminSharesView();
  const adminInvitesView = new AdminInvitesView();
  const adminRvideoView = new AdminRvideoView();
  const adminExtView = new AdminExtView();
  const adminCollectionsView = new AdminCollectionsView();
  const adminPathsView = new AdminPathsView();
  const adminStorageView = new AdminStorageView();
  const adminNodesView = new AdminNodesView();
  const discoverView = new DiscoverView();
  const rssView = new RssView();
  const historyView = new HistoryView();
  const settingsView = new SettingsView();
  const opsView = new OpsView();
  const zapdEngineView = new ZapdEngineView();
  const dashboardView = new DashboardView();
  const dedupView = new DedupView({ sidebar });

  // ── View registry ───────────────────────────────────────────────────
  // Single source of truth for the SPA's view system. Sidebar `data-view`
  // values map to one of these container IDs; switchView() hides every
  // container, then unhides exactly one. Earlier per-branch hide-lists
  // were inconsistent — switching from 'rss' to 'files' would leave
  // #rssview visible because the 'files' branch never listed it.
  const ALL_CONTAINER_IDS = [
    'table',
    'fileview',
    'libraryview',
    'musicview',
    'photoview',
    'adminusersview',
    'adminsharesview',
    'admininvitesview',
    'adminrvideoview',
    'adminextview',
    'admincollectionsview',
    'adminpathsview',
    'adminstorageview',
    'adminnodesview',
    'discoverview',
    'rssview',
    'historyview',
    'settingsview',
    'dashboardview',
    'opsview',
    'dedupview',
    'zapdengineview',
    'noaccessview',
  ];
  const VIEW_CONTAINERS = {
    active: 'table',
    all: 'table',
    trash: 'table',
    files: 'fileview',
    library: 'libraryview',
    adult: 'libraryview',
    private: 'libraryview',
    music: 'musicview',
    photos: 'photoview',
    'admin-users': 'adminusersview',
    'admin-shares': 'adminsharesview',
    'admin-invites': 'admininvitesview',
    'admin-rvideo': 'adminrvideoview',
    'admin-ext': 'adminextview',
    'admin-collections': 'admincollectionsview',
    'admin-paths': 'adminpathsview',
    'admin-storage': 'adminstorageview',
    'admin-nodes': 'adminnodesview',
    discover: 'discoverview',
    rss: 'rssview',
    history: 'historyview',
    settings: 'settingsview',
    dashboard: 'dashboardview',
    ops: 'opsview',
    dedup: 'dedupview',
    'zapd-engine': 'zapdengineview',
    'no-access': 'noaccessview',
  };
  const TORRENT_VIEWS = new Set(['active', 'all', 'trash']);
  // Views worth popping into their own OS window (#87, Document PiP). Their
  // container node is moved out live, so only self-contained content views
  // qualify — not torrent-table chrome or admin panels.
  const UNDOCKABLE_VIEWS = new Set(['files', 'library', 'adult', 'private', 'music', 'photos']);

  // Admins land on the torrent table; a user with zero content modules (a guest
  // or a not-yet-granted account) gets the "no access" state once the manifest
  // confirms it (B7b); everyone else lands on their library.
  const CONTENT_MODULES = [
    'mod.video',
    'mod.music',
    'mod.photos',
    'mod.spotify',
    'mod.dj',
    'mod.torrent',
    'mod.adult',
    'mod.private',
  ];
  let view;
  if (me?.role === 'admin') view = 'active';
  else if (caps.loaded() && !CONTENT_MODULES.some((c) => caps.has(c))) view = 'no-access';
  else view = 'library';
  let page = 1;
  let q = '';

  const torrents = new TorrentsView({
    selection,
    onPageChange: (p) => {
      page = p;
      reconnectStream();
    },
    onSearchChange: (newQ) => {
      q = newQ;
      page = 1; // any new search resets paging
      reconnectStream();
    },
    onSelect: () => toolbar.refresh(),
  });

  const toolbar = new Toolbar({
    selection,
    onAction: () => reconnectStream(),
  });
  selection.onChange(() => toolbar.refresh());

  // P0-A: the dash stream delivers the FULL torrent superset (all torrents,
  // tracker-whitelisted); the view/search/pagination are computed here instead
  // of via per-connection server params. selectTorrents() reproduces exactly
  // what fetchTorrentList()+streamLoop did server-side.
  let fullRows = [];
  const PER_PAGE = 50;
  function selectTorrents() {
    const qq = q.trim().toLowerCase();
    let rows = fullRows.filter((r) => {
      if (view === 'active') return r.active === 1;
      if (view === 'trash') return r.status === 'removed';
      // 'all' (Bibliothek): completed + not removed
      return r.status !== 'removed' && (r.percentage ?? 0) >= 100;
    });
    if (qq) {
      rows = rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(qq) || (r.tracker || '').toLowerCase().includes(qq),
      );
    }
    // active/all: newest-added first; trash: most-recently-zapped first.
    const sortKey = view === 'trash' ? 'zaptime' : 'addedSort';
    rows = rows
      .slice()
      .sort((a, b) => String(b[sortKey] || '').localeCompare(String(a[sortKey] || '')));
    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    page = Math.min(Math.max(1, page), totalPages);
    const start = (page - 1) * PER_PAGE;
    const slice = rows.slice(start, start + PER_PAGE);
    slice.forEach((r, i) => {
      r.absPos = start + i + 1;
    });
    return { rows: slice, total, totalPages, page, view };
  }
  function renderTorrents() {
    if (!TORRENT_VIEWS.has(view)) return;
    torrents.apply(selectTorrents());
  }

  // -------- view switching --------
  // Hide-all-then-show-one pattern. Each view also gets enter/leave hooks so
  // long-lived resources (ops EventSource, etc.) get torn down when leaving.
  const search = document.getElementById('search');
  const counter = document.getElementById('counter');
  const pager = document.querySelector('.pager');
  // The toolbar #sort-select operates on #table only; without hiding it
  // here, switching to Library/Files/etc. leaves a dead dropdown above
  // those views (Library has its own #lib-sort in the lib__bar).
  const sortSel = document.getElementById('sort-select');
  const setTorrentChrome = (visible) => {
    if (search) search.parentElement.style.display = visible ? '' : 'none';
    if (counter) counter.style.display = visible ? '' : 'none';
    if (pager) pager.style.display = visible ? '' : 'none';
    if (sortSel) sortSel.style.display = visible ? '' : 'none';
  };

  const VIEW_HOOKS = {
    active: { enter: enterTorrents, leave: () => torrents.closePlayer() },
    all: { enter: enterTorrents, leave: () => torrents.closePlayer() },
    trash: { enter: enterTorrents, leave: () => torrents.closePlayer() },
    files: { enter: () => filesView.load(), leave: () => filesView.closePlayer() },
    library: { enter: () => libraryView.load('main'), leave: () => libraryView.closePlayer() },
    adult: { enter: () => libraryView.load('adult'), leave: () => libraryView.closePlayer() },
    private: { enter: () => libraryView.load('private'), leave: () => libraryView.closePlayer() },
    music: { enter: () => musicView.load(), leave: () => musicView.closePlayer() },
    photos: { enter: () => photoView.load(), leave: () => photoView.closePlayer() },
    'admin-users': { enter: () => adminUsersView.load(), leave: noop },
    'admin-shares': { enter: () => adminSharesView.load(), leave: noop },
    'admin-invites': { enter: () => adminInvitesView.load(), leave: noop },
    'admin-rvideo': { enter: () => adminRvideoView.load(), leave: noop },
    'admin-ext': { enter: () => adminExtView.load(), leave: noop },
    'admin-collections': { enter: () => adminCollectionsView.load(), leave: noop },
    'admin-paths': { enter: () => adminPathsView.load(), leave: noop },
    'admin-storage': { enter: () => adminStorageView.load(), leave: noop },
    'admin-nodes': { enter: () => adminNodesView.load(), leave: noop },
    discover: { enter: () => discoverView.show(), leave: noop },
    rss: { enter: () => rssView.show(), leave: () => rssView.hide?.() },
    history: { enter: () => historyView.show(), leave: noop },
    settings: { enter: () => settingsView.show(), leave: noop },
    ops: { enter: () => opsView.load(), leave: () => opsView.destroy?.() },
    dashboard: { enter: () => dashboardView.load(), leave: () => dashboardView.destroy?.() },
    dedup: { enter: () => dedupView.load(), leave: () => dedupView.hide?.() },
    'zapd-engine': { enter: () => zapdEngineView.load(), leave: () => zapdEngineView.destroy?.() },
    'no-access': { enter: noop, leave: noop },
  };
  function noop() {}
  function enterTorrents() {
    torrents.reset();
    page = 1;
    q = '';
    if (search) search.value = '';
    reconnectStream();
  }

  function switchView(name) {
    // Settings is a modal <dialog> overlay, not a routable view: open it over
    // the current view and stay put, so closing it returns where you were.
    if (name === 'settings') {
      settingsView.show();
      return;
    }
    if (!VIEW_CONTAINERS[name]) {
      // Unknown view (typo'd hash from a bookmark, etc.) — fall back silently.
      name = 'active';
    }
    // Dark-launch: music is hidden unless enabled (core/flags.js). Treat any
    // route to it (deep-link #music, popstate, saved hash) as unreachable so it
    // can't be opened without the local opt-in.
    if (name === 'music' && !gate('mod.music', MUSIC_ENABLED)) {
      name = me?.role === 'admin' ? 'active' : 'library';
    }
    const prev = view;

    const apply = () => {
      // Tear down the previous view (closes ops SSE, hides rss, ...).
      if (prev !== name) VIEW_HOOKS[prev]?.leave?.();

      // Hide every container, then unhide exactly the target.
      for (const id of ALL_CONTAINER_IDS) {
        const el = document.getElementById(id);
        if (el) el.hidden = true;
      }
      const targetEl = document.getElementById(VIEW_CONTAINERS[name]);
      if (targetEl) targetEl.hidden = false;

      // Toolbar/pager/search are torrent-only chrome.
      setTorrentChrome(TORRENT_VIEWS.has(name));

      // Commit state + drive the new view.
      view = name;
      sidebar.setView(name);
      VIEW_HOOKS[name].enter();
      updateUndockBtn();
    };

    if (document.startViewTransition) {
      document.startViewTransition(apply);
    } else {
      apply();
    }

    // Deep-link sync — keep the URL fragment in sync with the active view
    // so bookmarks and back/forward work. We only push when the target
    // differs from what's currently in the bar (avoid stacking duplicates
    // on repeated clicks).
    const wantHash = '#' + name;
    if (location.hash !== wantHash) {
      history.pushState({ view: name }, '', wantHash);
    }
  }

  // back/forward navigation through the sidebar history
  window.addEventListener('popstate', (e) => {
    const target =
      (e.state && e.state.view) || (location.hash ? location.hash.slice(1) : null) || view;
    if (VIEW_CONTAINERS[target] && target !== view) switchView(target);
  });

  // Dark-launch: drop the "Musik" sidebar entry entirely unless enabled
  // (core/flags.js). Default OFF → invisible for everyone who pulls/deploys;
  // locally opt in via localStorage('zap.music','1'). switchView() also guards
  // the route, so deep-links/popstate can't reach the hidden view.
  if (!gate('mod.music', MUSIC_ENABLED)) {
    document.querySelectorAll('[data-view="music"]').forEach((el) => el.remove());
  }

  // Initial routing: if the page was opened with #<view>, jump there;
  // otherwise replaceState so the first switchView push has somewhere to
  // go back to.
  const initialHash = location.hash ? location.hash.slice(1) : '';
  if (initialHash && VIEW_CONTAINERS[initialHash] && initialHash !== view) {
    switchView(initialHash);
  } else {
    history.replaceState({ view }, '', '#' + view);
    // B7b: a guest has no nav to click, so its "no access" view (which isn't the
    // default-visible container) must be rendered explicitly at boot. Other
    // landing views keep their existing first-paint behaviour untouched.
    if (view === 'no-access') switchView(view);
  }

  // -------- stream lifecycle (P0-A: persistent dash feed) --------
  // The torrent dashboard is no longer a per-connection php-fpm SSE. ONE
  // always-on stream (/api/dash-events, fed by zapd's EventBroker) delivers the
  // full torrent superset + vitals + counts + storage; view/page/search are
  // computed client-side (selectTorrents). reconnectStream() therefore no
  // longer reconnects — it just re-renders the current selection from cache.
  function reconnectStream() {
    renderTorrents();
  }

  // The dash producer publishes ONE combined envelope per tick (zapd relays it
  // as a default `message` event, not the named per-type events the old php
  // stream emitted). Route the envelope to the same view handlers.
  stream.on('message', (env) => {
    const d = (env && env.data) || {};
    if (d.snapshot) {
      fullRows = d.snapshot.rows || [];
      renderTorrents();
    }
    if (d.vitals) {
      kpi.apply(d.vitals);
      statusbar.apply(d.vitals);
    }
    if (d.counts) sidebar.applyCounts(d.counts);
  });
  stream.onOpen(() => statusbar.setStreamState('open'));
  stream.onClose(() => statusbar.setStreamState('closed'));

  // -------- filter toggles --------
  document.getElementById('tg-compact')?.addEventListener('change', (e) => {
    document.body.classList.toggle('is-compact', e.target.checked);
  });

  // -------- logout --------
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    auth.logout();
  });

  // -------- top-bar: undock current view into its own window (#87) --------
  // Self-contained on purpose (own DOM lookups) so the hoisted call inside
  // switchView()'s apply() is safe even during initial routing.
  function updateUndockBtn() {
    const btn = document.getElementById('topbar-undock');
    if (!btn) return;
    const out = isUndocked();
    const can = undockSupported() && UNDOCKABLE_VIEWS.has(view);
    // Never leave a dead button: hidden unless this view can undock, or a
    // window is currently out (so it can always be redocked).
    btn.hidden = !can && !out;
    btn.disabled = !can && !out;
    btn.classList.toggle('is-active', out);
    btn.title = out ? 'Fenster zurückdocken' : 'In eigenes Fenster ausdocken';
  }
  document.getElementById('topbar-undock')?.addEventListener('click', () => {
    const el = document.getElementById(VIEW_CONTAINERS[view]);
    if (el) toggleUndock(el, { title: 'Zap — ' + view }).then(updateUndockBtn);
  });
  // After a popout closes, re-assert the SPA's one-visible-view rule (the user
  // may have navigated elsewhere while it was out), then refresh the button.
  document.addEventListener('zap:redock', (e) => {
    const el = e.detail?.el;
    if (el) el.hidden = el.id !== VIEW_CONTAINERS[view];
    updateUndockBtn();
  });
  document.addEventListener('zap:undock', updateUndockBtn);

  // -------- top-bar: settings gear + account menu (#86b) --------
  document
    .getElementById('topbar-settings')
    ?.addEventListener('click', () => switchView('settings'));
  {
    const acct = document.getElementById('zap-account');
    const avatar = document.getElementById('topbar-avatar');
    const menu = document.getElementById('account-menu');
    if (acct && avatar && menu) {
      const setOpen = (open) => {
        menu.hidden = !open;
        avatar.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      avatar.addEventListener('click', (e) => {
        e.stopPropagation();
        setOpen(menu.hidden);
      });
      document.addEventListener('click', (e) => {
        if (!acct.contains(e.target)) setOpen(false);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') setOpen(false);
      });
    }
  }

  // -------- upload .torrent (button + drag-drop + magnet) --------
  const uploadBtn = document.getElementById('upload-btn');
  const uploadInput = document.getElementById('upload-input');

  /** Shared multipart upload — used by sidebar button AND drag-drop on #table. */
  async function uploadTorrentFiles(files, busyEl = uploadBtn) {
    const torrentFiles = Array.from(files).filter((f) => /\.torrent$/i.test(f.name));
    if (torrentFiles.length === 0) return;
    const fd = new FormData();
    for (const f of torrentFiles) fd.append('torrent[]', f, f.name);
    busyEl?.classList.add('is-busy');
    try {
      const r = await fetch('/api-zapd/upload', {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: { Authorization: 'Bearer ' + (auth.getToken() || '') },
      });
      const json = await r.json().catch(() => ({}));
      if (r.status === 401) {
        await auth.refresh().catch(() => {
          window.location = '/login';
        });
      }
      if (json.toast) toast[json.toast.type ?? 'info'](json.toast.msg);
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    } finally {
      busyEl?.classList.remove('is-busy');
    }
  }

  if (uploadBtn && uploadInput) {
    uploadBtn.addEventListener('click', () => uploadInput.click());
    uploadInput.addEventListener('change', async () => {
      await uploadTorrentFiles(uploadInput.files || []);
      uploadInput.value = '';
    });
  }

  // Drag-drop overlay on the torrents table — accepts .torrent files anywhere on the page.
  // Uses a counter to handle nested dragenter/leave reliably.
  let dragDepth = 0;
  const _tableHost = document.getElementById('content') || document.body;
  const dropOverlay = document.createElement('div');
  dropOverlay.className = 'zap-drop-overlay';
  dropOverlay.innerHTML =
    '<div class="zap-drop-overlay__inner">.torrent-Datei(en) hier ablegen</div>';
  document.body.appendChild(dropOverlay);

  const showOverlay = () => dropOverlay.classList.add('is-on');
  const hideOverlay = () => dropOverlay.classList.remove('is-on');

  // Only show overlay when the drag actually carries files.
  document.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
    dragDepth++;
    showOverlay();
  });
  document.addEventListener('dragleave', () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) hideOverlay();
  });
  document.addEventListener('dragover', (e) => {
    if (dropOverlay.classList.contains('is-on')) e.preventDefault();
  });
  document.addEventListener('drop', async (e) => {
    if (!dropOverlay.classList.contains('is-on')) return;
    e.preventDefault();
    dragDepth = 0;
    hideOverlay();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    const torrents = files.filter((f) => /\.torrent$/i.test(f.name));
    if (torrents.length === 0) {
      toast.warn('Nur .torrent-Dateien werden akzeptiert');
      return;
    }
    await uploadTorrentFiles(torrents);
    // Force-reconnect the stream so new torrents show up immediately.
    setTimeout(() => reconnectStream(), STREAM_RECONNECT_MS);
  });

  // S3 Serien: "Import" — .torrent nur laden + nach Download in die Mediathek
  // einsortieren (source='import' → Auto-Organize). Bewusst OHNE Player-Route
  // (der "+"-Flow streamt; dieser hier ist der Serien-/Library-Eingang).
  const importBtn = document.getElementById('import-btn');
  const importFile = document.getElementById('import-file');
  importBtn?.addEventListener('click', () => {
    const nativeDialog = globalThis.zapShell?.openTorrentDialog;
    if (typeof nativeDialog === 'function') {
      nativeDialog({ import: true }).catch((e) =>
        toast.error('Import-Dialog: ' + (e?.message || e)),
      );
      return;
    }
    importFile?.click();
  });
  importFile?.addEventListener('change', async () => {
    const files = [...(importFile.files || [])];
    importFile.value = '';
    if (!files.length) return;
    for (const f of files) {
      try {
        await addTorrentBlob(f, { import: true });
        toast.success(`${f.name}: lädt — wird nach Download einsortiert`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.message || e}`);
      }
    }
    setTimeout(() => reconnectStream(), STREAM_RECONNECT_MS);
  });

  // Add Magnet button + modal
  document.getElementById('magnet-btn')?.addEventListener('click', async () => {
    showModal({
      title: 'Magnet hinzufügen',
      saveLabel: 'Hinzufügen',
      body: `
        <label class="form-row"><span>Magnet-URI</span>
          <textarea id="mg-uri" rows="3" style="width:100%;font-family:monospace;font-size:12px"
            placeholder="magnet:?xt=urn:btih:..." autofocus></textarea></label>
        <label class="form-row"><span>Label (optional)</span>
          <input id="mg-label" type="text" placeholder="Series/Show"></label>
        <label class="form-row"><span>Sofort starten</span>
          <input id="mg-start" type="checkbox" checked></label>
      `,
      onSave: async () => {
        const uri = document.getElementById('mg-uri').value.trim();
        if (!uri.toLowerCase().startsWith('magnet:?')) {
          toast.error('Magnet-URI muss mit "magnet:?" beginnen');
          return false;
        }
        try {
          const result = await addMagnet(uri);
          const id = Number(result.torrent_id || result.id || 0);
          const label = document.getElementById('mg-label').value.trim();
          if (id && label) {
            await api(`/api-zapd/v1/torrent/${id}/label`, {
              method: 'PUT',
              body: { label },
            });
          }
          if (id && !document.getElementById('mg-start').checked) {
            await api(`/api-zapd/v1/torrent/${id}/pause`, { method: 'POST', body: {} });
          }
          toast.success('Magnet hinzugefügt');
          setTimeout(() => reconnectStream(), STREAM_RECONNECT_MS);
          return true;
        } catch (e) {
          toast.error('Fehler: ' + (e.message || e));
          return false;
        }
      },
    });
  });

  // Sort dropdown — applies CLIENT-SIDE to current snapshot rows in #table.
  // (Server-side sort would need backend changes; for one-page view this is plenty.)
  document.getElementById('sort-select')?.addEventListener('change', (e) => {
    const mode = e.target.value;
    const tbl = document.getElementById('table');
    if (!tbl) return;
    if (mode === 'default') {
      reconnectStream(); // simplest reset: refetch
      return;
    }
    // Rows live in #table > .rows > .row (the .rows wrapper is the actual parent).
    const rowsContainer = tbl.querySelector('.rows') || tbl;
    const rows = Array.from(rowsContainer.querySelectorAll(':scope > .row'));
    const getNum = (el, sel) => {
      const t = el.querySelector(sel)?.textContent?.trim() || '';
      const m = t.match(/[\d.,]+/);
      if (!m) return 0;
      let n = parseFloat(m[0].replace(/\./g, '').replace(',', '.')) || 0;
      if (/Ki?B/i.test(t)) n *= 1024;
      else if (/Mi?B/i.test(t)) n *= 1024 ** 2;
      else if (/Gi?B/i.test(t)) n *= 1024 ** 3;
      else if (/Ti?B/i.test(t)) n *= 1024 ** 4;
      return n;
    };
    // data-added is an ISO-ish string ("YYYY-MM-DD HH:MM:SS"), so a plain
    // string compare gives correct chronological order without parsing.
    const dateOf = (el) => el.dataset.added || '';
    const cmp = {
      'name-asc': (a, b) =>
        (a.querySelector('.row__title')?.textContent || '').localeCompare(
          b.querySelector('.row__title')?.textContent || '',
        ),
      'name-desc': (a, b) =>
        (b.querySelector('.row__title')?.textContent || '').localeCompare(
          a.querySelector('.row__title')?.textContent || '',
        ),
      'date-desc': (a, b) => dateOf(b).localeCompare(dateOf(a)),
      'date-asc': (a, b) => dateOf(a).localeCompare(dateOf(b)),
      'size-desc': (a, b) => getNum(b, '.row__size') - getNum(a, '.row__size'),
      'size-asc': (a, b) => getNum(a, '.row__size') - getNum(b, '.row__size'),
      'pct-desc': (a, b) => getNum(b, '.row__pct') - getNum(a, '.row__pct'),
      'pct-asc': (a, b) => getNum(a, '.row__pct') - getNum(b, '.row__pct'),
      'dl-desc': (a, b) =>
        getNum(b, '.row__dl, .row__speed-dl') - getNum(a, '.row__dl, .row__speed-dl'),
      'ul-desc': (a, b) =>
        getNum(b, '.row__ul, .row__speed-ul') - getNum(a, '.row__ul, .row__speed-ul'),
    }[mode];
    if (!cmp) return;
    rows.sort(cmp).forEach((r) => rowsContainer.appendChild(r));
  });

  // -------- focus highlight (from ?focus=...) --------
  const focusHash =
    document.querySelector('meta[name="zap-focus"]')?.content ||
    window.ZAP_CONFIG?.focus; /* legacy fallback during transition */
  if (focusHash) {
    view = 'all';
    sidebar.setView('all');
    setTimeout(() => {
      const row = document.getElementById('row-' + focusHash);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row.classList.add('is-focus-flash');
        setTimeout(() => row.classList.remove('is-focus-flash'), FOCUS_FLASH_MS);
      }
    }, 1500);
  }

  // -------- cover hover preview --------
  // Single floating element on body, delegated handlers on the table
  mountCoverHover(document.getElementById('table') || document);

  // -------- bootstrap --------
  // Initial counts so sidebar isn't blank during first stream connect.
  // For non-admins this is also their ONLY source: the dash stream is admin-
  // only (carries torrent data + full admin counters), so they don't connect.
  api('counts')
    .then((c) => sidebar.applyCounts(c))
    .catch((e) => console.error('[sidebar] counts load failed', e));
  // P0-A: connect the persistent dash stream ONCE. The torrent superset feed is
  // gated on mod.torrent (B7) — admins and granted moderators get it; until the
  // manifest is live, gate() falls back to the admin role (prior behaviour).
  // view/page/q changes re-render from cache (renderTorrents) — no reconnect.
  if (gate('mod.torrent', me?.role === 'admin')) {
    statusbar.setStreamState('connecting');
    stream.connect('/api-zapd/dash-events');
  }
}
